import React, { useRef, useState, useEffect } from 'react';
import { ArrowRight, CheckCircle, Rocket, Code, Smartphone, PenTool, Cloud, ArrowUpRight, Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useSiteContent } from '../context/SiteContext';
import { LazyImage } from '../components/LazyImage';

export const Home: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const navigate = useNavigate();
  const [email, setEmail] = useState('');

  // Consume Context
  const { projects, services, siteContent, t, language } = useSiteContent();
  const content = siteContent.home;
  console.log('Home Rendering. Projects:', projects.length);

  const activeProjects = projects.filter(p => p.isActive).slice(0, 4);
  const activeServices = services.filter(s => s.isActive).slice(0, 4);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleCtaSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate('/contact');
  };

  const getIcon = (name: string) => {
    switch (name) {
      case 'Smartphone': return <Smartphone size={24} />;
      case 'PenTool': return <PenTool size={24} />;
      case 'Cloud': return <Cloud size={24} />;
      default: return <Code size={24} />;
    }
  };

  // Spotlight Effect Logic
  const handleMouseMove = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const { currentTarget: target } = e;
    const rect = target.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    target.style.setProperty("--mouse-x", `${x}px`);
    target.style.setProperty("--mouse-y", `${y}px`);
  };

  return (
    <>
      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 md:py-24 lg:py-32">
        {/* Animated Background Blobs */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10 pointer-events-none opacity-40 dark:opacity-20">
          <div className="absolute top-0 left-1/4 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
          <div className="absolute top-0 right-1/4 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-1/2 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-4000"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-16 lg:grid-cols-2 lg:gap-12 items-center">

            {/* Hero Text */}
            <div className="flex flex-col gap-8 text-center lg:text-left items-center lg:items-start z-10">
              <div className="inline-flex w-fit items-center gap-2 rounded-full border border-gray-200 dark:border-white/10 bg-white/50 dark:bg-white/5 backdrop-blur-sm px-4 py-1.5 text-xs font-bold text-slate-600 dark:text-gray-300 shadow-sm transition-transform hover:scale-105">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                {t.home.reportBadge}
              </div>

              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.05] tracking-tight text-slate-900 dark:text-white">
                {content.heroTitle.split(' ').map((word, i) => (
                  <span key={i} className={i === 1 ? 'text-primary' : ''}>{word} </span>
                ))}
              </h1>

              <p className="max-w-xl text-lg md:text-xl text-slate-600 dark:text-gray-400 leading-relaxed font-light">
                {content.heroSubtitle}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mt-2 w-full sm:w-auto">
                <Link to="/projects" className="w-full sm:w-auto">
                  <button className="group h-14 w-full sm:w-auto min-w-[180px] rounded-2xl bg-primary px-8 text-base font-bold text-white shadow-xl shadow-primary/30 hover:bg-primary-hover hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-2">
                    {t.home.btnReview} <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </Link>
                <Link to="/contact" className="w-full sm:w-auto">
                  <button className="h-14 w-full sm:w-auto min-w-[180px] rounded-2xl border border-gray-300 dark:border-border-dark bg-white/50 dark:bg-white/5 backdrop-blur px-8 text-base font-bold text-slate-900 dark:text-white hover:bg-gray-100 dark:hover:bg-white/10 hover:-translate-y-1 transition-all duration-300">
                    {t.home.btnContact}
                  </button>
                </Link>
              </div>
            </div>

            {/* Hero Video Card */}
            <div className="relative w-full max-w-lg lg:max-w-none mx-auto lg:mx-0 perspective-1000">
              <div className="relative aspect-[4/3] w-full overflow-hidden rounded-[2.5rem] bg-gray-100 dark:bg-surface-dark shadow-2xl border-4 border-white dark:border-white/5 group transform transition-transform duration-500 hover:rotate-1 hover:scale-[1.02]">
                <video
                  ref={videoRef}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="absolute inset-0 h-full w-full object-cover opacity-90 dark:opacity-80"
                  poster="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80&w=1200"
                >
                  <source src={content.heroVideoUrl} type="video/mp4" />
                </video>

                {/* Video Overlay Gradient */}
                <div className="absolute inset-0 bg-gradient-to-tr from-primary/40 to-transparent mix-blend-overlay pointer-events-none"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none"></div>

                {/* Floating Status Card */}
                <div className="absolute bottom-6 left-6 right-6 p-5 rounded-2xl bg-white/90 dark:bg-black/80 backdrop-blur-xl border border-white/20 dark:border-white/10 flex items-center justify-between shadow-lg transform transition-transform group-hover:translate-y-[-5px]">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-primary flex items-center justify-center text-white shrink-0 shadow-lg shadow-primary/40">
                      <Rocket size={24} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-900 dark:text-white">{t.common.systemStatus}</p>
                      <p className="text-xs text-green-500 font-bold flex items-center gap-1">
                        <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span> %99.9 Uptime
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button onClick={togglePlay} className="p-3 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 text-slate-900 dark:text-white transition-colors">
                      {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                    </button>
                    <button onClick={toggleMute} className="p-3 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 text-slate-900 dark:text-white transition-colors">
                      {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Decorative elements behind video */}
              <div className="absolute -z-10 -top-10 -right-10 w-32 h-32 bg-yellow-400 rounded-full blur-2xl opacity-20 animate-pulse"></div>
              <div className="absolute -z-10 -bottom-10 -left-10 w-40 h-40 bg-primary rounded-full blur-2xl opacity-20 animate-pulse delay-700"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Tech Stack Ticker */}
      <section className="border-y border-gray-200 dark:border-border-dark bg-white dark:bg-black/20 py-10 overflow-hidden">
        <div className="container mx-auto px-4">
          <p className="text-center text-xs font-bold uppercase tracking-[0.2em] text-gray-400 mb-8">{t.common.techStackTitle}</p>
          <div className="flex flex-wrap justify-center gap-x-12 gap-y-8 opacity-70 grayscale hover:grayscale-0 transition-all duration-700">
            {content.techStack.map((tech) => (
              <div key={tech} className="flex items-center gap-3 text-xl font-bold text-slate-800 dark:text-gray-300">
                <div className="w-2 h-2 rounded-full bg-primary/50"></div> {tech}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview - Spotlight Effect */}
      <section className="py-20 md:py-32 bg-gray-50/50 dark:bg-[#0c0c0f]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16 max-w-2xl">
            <h2 className="text-4xl font-black tracking-tight text-slate-900 dark:text-white sm:text-5xl mb-6">{content.servicesTitle}</h2>
            <p className="text-xl text-slate-600 dark:text-gray-400 font-light leading-relaxed">
              {content.servicesSubtitle}
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {activeServices.map((service) => (
              <Link
                to="/services"
                key={service.id}
                onMouseMove={handleMouseMove}
                className="spotlight-card group relative overflow-hidden rounded-3xl bg-white dark:bg-surface-dark border border-gray-200 dark:border-border-dark p-8 transition-all hover:shadow-2xl hover:shadow-primary/5 hover:-translate-y-2 block"
              >
                <div className="spotlight-bg"></div>
                <div className="relative z-10 flex flex-col h-full">
                  <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-50 dark:bg-white/5 text-primary border border-gray-100 dark:border-white/10 group-hover:bg-primary group-hover:text-white group-hover:border-primary transition-all duration-300 shadow-sm">
                    {getIcon(service.iconName)}
                  </div>
                  <h3 className="mb-3 text-xl font-bold text-slate-900 dark:text-white">{service.title[language]}</h3>
                  <p className="text-sm leading-relaxed text-slate-500 dark:text-gray-400">{service.desc[language]}</p>

                  <div className="mt-auto pt-6 border-t border-gray-100 dark:border-white/5">
                    <span className="text-sm font-bold text-primary flex items-center gap-2">
                      {t.common.details} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Project Carousel */}
      <section className="py-20 md:py-32 overflow-hidden bg-white dark:bg-background-dark border-t border-gray-100 dark:border-border-dark">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <h2 className="text-4xl font-black tracking-tight text-slate-900 dark:text-white sm:text-5xl">{t.home.latestWork}</h2>
              <p className="mt-4 text-xl text-slate-600 dark:text-gray-400 font-light">{t.projectsPage.portfolio}</p>
            </div>
            <div className="hidden sm:flex gap-3">
              <Link to="/projects" className="h-14 w-14 rounded-full border border-gray-200 dark:border-border-dark flex items-center justify-center text-slate-900 dark:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors">
                <ArrowRight className="rotate-180" size={24} />
              </Link>
              <Link to="/projects" className="h-14 w-14 rounded-full bg-primary text-white flex items-center justify-center hover:bg-primary-hover shadow-lg shadow-primary/25 transition-colors">
                <ArrowRight size={24} />
              </Link>
            </div>
          </div>

          <div className="flex gap-8 overflow-x-auto pb-12 -mx-4 px-4 sm:mx-0 sm:px-0 snap-x snap-mandatory scrollbar-hide">
            {activeProjects.map((proj) => (
              <div key={proj.id} className="min-w-[85vw] sm:min-w-[450px] snap-center first:pl-0 last:pr-4 group cursor-pointer" onClick={() => navigate(`/projects/${proj.id}`)}>
                <div className="relative overflow-hidden rounded-3xl bg-gray-100 dark:bg-surface-dark aspect-[16/10] shadow-sm mb-6">
                  {/* FIXED: Moved absolute positioning to containerClassName to avoid zero-height collapse */}
                  <LazyImage
                    src={proj.image}
                    alt={proj.title}
                    containerClassName="absolute inset-0 w-full h-full"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300"></div>

                  {/* Floating badge */}
                  <div className="absolute top-6 left-6 z-10">
                    <span className="px-3 py-1 bg-white/90 dark:bg-black/80 backdrop-blur text-xs font-bold rounded-lg text-slate-900 dark:text-white border border-white/20">
                      {proj.category[language]}
                    </span>
                  </div>

                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-primary shadow-2xl transform scale-50 group-hover:scale-100 transition-transform duration-300">
                      <ArrowUpRight size={28} />
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-white group-hover:text-primary transition-colors">{proj.title[language]}</h3>
                  <p className="text-slate-500 dark:text-gray-400 mt-2 line-clamp-2">{proj.description[language]}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center sm:hidden">
            <Link to="/projects" className="inline-flex items-center gap-2 text-primary font-bold text-lg">
              {t.common.allProjects} <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 px-4">
        <div className="container mx-auto">
          <div className="relative overflow-hidden rounded-[3rem] bg-primary px-6 py-20 text-center shadow-2xl shadow-primary/40 md:px-12 lg:py-28 group">

            {/* Background Texture */}
            <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] group-hover:scale-105 transition-transform duration-1000"></div>

            {/* Gradient Glows */}
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-white opacity-10 blur-[100px] rounded-full pointer-events-none"></div>

            <div className="relative z-10 mx-auto max-w-4xl">
              <h2 className="mb-8 text-4xl font-black tracking-tight text-white sm:text-5xl lg:text-7xl">
                {content.ctaTitle}
              </h2>
              <p className="mb-12 text-xl font-medium text-white/80 max-w-2xl mx-auto leading-relaxed">
                {content.ctaDesc}
              </p>
              <form onSubmit={handleCtaSubmit} className="flex flex-col items-center justify-center gap-4 sm:flex-row max-w-lg mx-auto">
                <input
                  className="h-16 w-full rounded-2xl border-2 border-white/20 bg-white/10 px-6 text-lg text-white placeholder:text-white/60 backdrop-blur-md focus:border-white focus:bg-white/20 focus:outline-none transition-all"
                  placeholder={t.home.emailPlaceholder}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <button type="submit" className="h-16 w-full sm:w-auto px-10 rounded-2xl bg-white text-lg font-bold text-primary hover:bg-gray-100 active:scale-95 transition-all shadow-xl">
                  {t.nav.getQuote}
                </button>
              </form>
              <p className="mt-6 text-white/50 text-sm">{t.home.freeConsult}</p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};